import {useCallback, useContext, useEffect, useState} from 'react';

import isEmpty from 'lodash.isempty';
import {MessageContext} from 'src/contexts/message-context';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {getPresetEntitlementList} from 'src/modules/entitlements/utils/getPresetEntitlementList';
import {getGrantsByIds} from 'src/openapi/admin/apis/EntitlementsGrantApi';
import {getResourcesByIds} from 'src/openapi/admin/apis/EntitlementsResourceApi';
import {getRoleNamesByIds} from 'src/openapi/admin/apis/EntitlementsRoleApi';
import {SortDirection} from 'src/openapi/admin/models/SortDirection';
import {useAppDispatch} from 'src/store/hooks';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import {useAbortSignal} from '@pb/kit-hooks-use-abort-signal';

import type {ResourceView} from 'src/modules/entitlements/typedef';
import type {EntitlementResource} from 'src/openapi/admin/models/EntitlementResource';
import type {Grant} from 'src/openapi/admin/models/Grant';
import type {PresetRelationItem} from 'src/openapi/admin/models/PresetRelationItem';
import type {Role} from 'src/openapi/admin/models/Role';

type Props = {
    relations: PresetRelationItem[],
};

export const useGetPresetEntitlements = ({relations}: Props) => {
    const apiDispatch = useAppDispatch();
    const {createAbortSignal} = useAbortSignal();
    const {setErrorMessage} = useContext(MessageContext);

    const [isLoading, setIsLoading] = useState(false);
    const [resourceViews, setResourceViews] = useState<ResourceView[]>([]);
    const [grants, setGrants] = useState<Record<string, Grant>>({});
    const [roles, setRoles] = useState<Record<string, Role>>({});
    const [resources, setResources] = useState<Record<string, EntitlementResource>>({});

    const getView = useCallback(async (rels: PresetRelationItem[]) => {
        setIsLoading(true);
        const signal = createAbortSignal();
        const roleIds = [...new Set(rels
            .filter(({roleId}) => roleId)
            .map(({roleId}) => roleId || ''),
        )];
        const rolesResponse = await apiDispatch(getRoleNamesByIds(
            {requestBody: roleIds},
            {signal},
        ));
        if (rolesResponse.payload.success) {
            setRoles(rolesResponse.payload.data);
        } else {
            setErrorMessage(rolesResponse.payload.data?.message || '');
        }

        const grantIds = [...new Set(rels
            .map(({grantId}) => grantId),
        )];
        const grantsResponse = await apiDispatch(getGrantsByIds(
            {requestBody: grantIds},
            {signal},
        ));
        if (grantsResponse.payload.success) {
            setGrants(grantsResponse.payload.data);
        } else {
            setErrorMessage(grantsResponse.payload.data?.message || '');
        }

        const resourceIds = [...new Set(rels
            .map(({resourceId}) => resourceId),
        )];
        const resourcesResponse = await apiDispatch(getResourcesByIds(
            {requestBody: resourceIds},
            {signal},
        ));
        if (resourcesResponse.payload.success) {
            setResources(resourcesResponse.payload.data);
        } else {
            setErrorMessage(resourcesResponse.payload.data?.message || '');
        }
        setIsLoading(false);
    }, [apiDispatch, setErrorMessage, createAbortSignal]);

    useEffect(() => {
        getView(relations);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffectWithDeps((prev, current) => {
        if (!isDeepEqual({
            prev: prev.map(({roleId, grantId, resourceId}) => ({roleId, grantId, resourceId})),
            current: current.map(({roleId, grantId, resourceId}) => ({roleId, grantId, resourceId})),
        })) {
            getView(current);
        }
    }, relations);

    useEffect(() => {
        if (!isLoading) {
            setResourceViews(relations.length && !isEmpty(grants) && !isEmpty(resources) ? getPresetEntitlementList({
                roles,
                grants,
                resources,
                relations,
                sortDirection: SortDirection.ASC,
            }) : []);
        }
    }, [roles, grants, resources, relations, isLoading]);

    return {resourceViews};
};
